import { FC, useEffect, useState } from 'react'

import { SharedModalContent, SharedModalInputs } from 'views/components/SharedModal'
import { useTranslation } from 'react-i18next'
import { ProfileType, User } from 'modules/domain/user/types'
import { BidAddFields, BidKind, BidPrice, BidTags, BidTypes } from 'modules/domain/bid/types'
import { FormGroup, Input, defaultTheme, Text, Progress, Spinner } from '@agro-club/agroclub-shared'
import { WarningContainer } from 'views/pages/Bid/BidModals/styled'
import { CommonBidModalControls } from '../CommonBidModalControls'
import { ShortFormControl } from 'views/styled/common'
import { QuantityInput } from 'views/components/QuantityInput/QuantityInput'
import { labelTarif } from 'modules/utils/numbers/formatTarif'
import ADefaultModalFooter from 'views/components/DefaultModalFooter/ADefaultModalFooter'
import { BidParamsInputs } from './BidParamsInputs'
import { isBidProductParamsEnabled } from 'env'
import { Product } from 'modules/domain/collection/types'
import { generatePath, useNavigate } from 'react-router'
import { apiClient } from 'modules/utils/httpClient'
import BidRoutes from '../../routes'
import { endpoints } from 'modules/endpoints'
import { useFormik } from 'formik'
import FormSelectShared from 'views/components/FormSelectShared'
import { SeasonSelect } from '../../components/SeasonSelect'
import { useAFormHandler } from 'analytics/hooks'
import { useMediaQuery } from 'react-responsive'
import { MobileModalFooter } from 'views/components/MobileComponents/Modal/MobileModalFooter'
import { useTableData } from 'modules/domain/common/hooks'
import { SampleDetailed } from 'views/pages/Samples/types'
import { ListRequestParams } from 'modules/domain/types'
import { defaultListRequestParams } from 'views/components/TableFilters/data'
import React from 'react'
import { ARouterLink } from 'views/components/Analytics'
import SamplesRoutes from 'views/pages/Samples/routes'
import styled from 'styled-components'
import useFormatDate from 'hooks/useFormatDate'

export const InitialMainParameter = {
  parameter: undefined,
  parameter_value_from: undefined,
  parameter_value_to: undefined,
}

export type BidFormProps = {
  user?: User
  onClose: () => void
  bidKind: BidKind
  onSuccess?: (value) => void
  children?: React.ReactNode
}

const SampleDataWrapper = styled.div`
  padding-left: 224px;
  padding-top: 4px;
`

export const BidAddForm: FC<BidFormProps> = ({ user, onClose, onSuccess, children }) => {
  const { t } = useTranslation('bid')
  const { formProgress, formHandler } = useAFormHandler('create')
  const [product, setProduct] = useState<Product | undefined>()
  const navigate = useNavigate()
  const formatDate = useFormatDate()

  const [ownerProfileType, setOwnerProfileType] = useState<ProfileType | undefined>(user?.profile?.profile_type)

  const [samplesParams, setSamplesParams] = useState<ListRequestParams>({ ...defaultListRequestParams })
  const { progress: samplesProgress, data: samples } = useTableData<SampleDetailed>(
    endpoints.samples(),
    samplesParams,
    !!samplesParams.filter.product_id && !!samplesParams.filter.address,
  )

  const samplesData = samples?.map((s) => (
    <div key={s.id}>
      <ARouterLink
        eventName={'SampleLink'}
        target="_blank"
        to={generatePath(SamplesRoutes.Details, {
          id: s.id,
        })}
      >
        {s.published_at
          ? `${t('form.sampleFrom')} ${formatDate(s.published_at)}`
          : `${t('form.sampleUnfinished')} ${formatDate(s.created)}`}
      </ARouterLink>
    </div>
  ))

  const navigateToNewBid = (id: string) => {
    onClose()
    navigate(generatePath(BidRoutes.Details, { id }))
  }

  const formik = useFormik<BidAddFields>({
    initialValues: {
      bid_type: ownerProfileType === ProfileType.customer ? BidTypes.purchase : BidTypes.sale,
      company: user?.profile?.company?.id || undefined,
      owner: user?.id ? String(user.id) : undefined,
      address: undefined,
      product: undefined,
      price: undefined,
      price_type: undefined,
      quantity: undefined,
      parameters: [],
      mainParameter: InitialMainParameter,
      owner_delivery_address: undefined,
      season: undefined,
      use_season_in_matching: undefined,
    },
    onSubmit: formHandler(
      async () => {
        const dto = {
          bid_type: formik.values.bid_type,
          company: formik.values.company,
          quantity: formik.values.quantity,
          product: formik.values.product,
          owner: formik.values.owner,
          address: formik.values.address,
          price: formik.values.price,
          price_type: formik.values.price_type,
          bid_tag: formik.values.bid_tag,
          owner_delivery_address: formik.values.owner_delivery_address,
          parameters: [formik.values.mainParameter, ...formik.values.parameters],
          season: formik.values.season,
          use_season_in_matching: formik.values.use_season_in_matching,
        }

        if (!isBidProductParamsEnabled) {
          dto['parameters'] = []
        }

        return await apiClient.post<{ id: string }>(endpoints.bid(), dto)
      },
      {
        onSuccess: async (requestResult) => {
          if (onSuccess) {
            await onSuccess(requestResult)
          } else {
            await navigateToNewBid(requestResult.id)
          }
        },
        onError: (error) => {
          const { errors } = error
          if (errors?.use_season_in_matching) {
            errors['season'] = errors.use_season_in_matching
          }
          formik.setErrors(errors)
        },
      },
    ),
  })

  useEffect(() => {
    if (!product) {
      return
    }
    formik.setValues({
      ...formik.values,
      parameters: [],
      mainParameter: {
        parameter: product.main_parameter?.id,
        parameter_value_from: undefined,
        parameter_value_to: undefined,
      },
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product])

  useEffect(() => {
    if (!ownerProfileType) return

    if (ownerProfileType === ProfileType.customer) {
      formik.setFieldValue('bid_type', BidTypes.purchase)
      formik.setFieldValue('bid_tag', BidTags.EXPORT)
    } else {
      formik.setFieldValue('bid_type', BidTypes.sale)
      formik.setFieldValue('bid_tag', undefined)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ownerProfileType])

  useEffect(() => {
    if (!formik.values.bid_type) return

    const priceType = formik.values.bid_type === BidTypes.purchase ? BidPrice.CPT : BidPrice.EXW
    formik.setFieldValue('price_type', priceType)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.bid_type])

  const isMobile = useMediaQuery({
    query: `(max-width: ${defaultTheme.queries.mobile})`,
  })

  return (
    <>
      <SharedModalContent>
        <WarningContainer>
          <div>{t('form.createWarning')}</div>
        </WarningContainer>
        <SharedModalInputs>
          {children}
          <CommonBidModalControls
            setOwnerProfileType={setOwnerProfileType}
            ownerProfileType={ownerProfileType}
            formik={formik}
            onAddressChange={(value) => {
              setSamplesParams({
                ...samplesParams,
                filter: { ...samplesParams.filter, address: value },
              })
            }}
          />
          <div>
            <FormSelectShared
              label={t('form.product')}
              placeholder={t('form.selectProductPlaceholder')}
              formik={formik}
              onClear={() => setProduct(undefined)}
              onSelectedOptionLoaded={(e) => setProduct(e.dto)}
              endpoint={endpoints.products()}
              simpleApiFormat
              getLabel={(dto) => dto.title}
              onChange={(value) => {
                formik.setFieldValue('season', undefined)
                formik.setFieldValue('product', value)
                setSamplesParams({
                  ...samplesParams,
                  filter: { ...samplesParams.filter, product_id: value },
                })
              }}
              fieldName="product"
              showSearch={false}
              required
            />
            <SampleDataWrapper>
              {!!samplesData?.length && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <Text color="secondary">{t('form.samplesExists')}</Text>
                  {samplesData}
                </div>
              )}
              {!!samplesData?.length && samplesProgress === Progress.WORK && <Spinner size="small" />}
            </SampleDataWrapper>
          </div>
          <SeasonSelect
            formik={formik}
            disabled={!formik.values.product}
            showMatchingCheckBox={ownerProfileType === ProfileType.customer}
          />
          <ShortFormControl>
            <FormGroup label={labelTarif(t('common:price.default'))} error={formik.errors.price} required>
              <Input {...formik.getFieldProps('price')} invalid={!!formik.errors.price} type={'number'} />
            </FormGroup>
          </ShortFormControl>
          <ShortFormControl>
            <QuantityInput formik={formik} fieldName="quantity" label={t('common:volume')} required />
          </ShortFormControl>
        </SharedModalInputs>
        <BidParamsInputs formik={formik} product={product} />
      </SharedModalContent>
      {isMobile ? (
        <MobileModalFooter
          onConfirm={() => formik.handleSubmit()}
          confirmButtonText={t('common:add')}
          disabled={!formik.dirty}
          progress={formProgress}
          onClose={onClose}
        />
      ) : (
        <ADefaultModalFooter
          onConfirm={() => formik.handleSubmit()}
          confirmButtonText={t('common:add')}
          disabled={!formik.dirty}
          progress={formProgress}
          onClose={onClose}
        />
      )}
    </>
  )
}
